import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, Menu, X } from 'lucide-react';

const Header11 = ({ cartCount = 0 }) => {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [mobileMenuOpen]);

    const navLinks = [
        { label: 'HOME', path: '/template11' },
        { label: 'SHOP', path: '/template11/shop' },
        { label: 'ABOUT', path: '/template11/about' },
        { label: 'CONTACT', path: '/template11/contact' },
        { label: 'ORDERS', path: '/template11/orders' },
    ];

    const handleNavClick = (path) => {
        navigate(path);
        setMobileMenuOpen(false);
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&family=Source+Sans+3:wght@300;400;600&display=swap');
            `}</style>

            <header
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 50,
                    backgroundColor: scrolled ? '#1C1917' : 'transparent',
                    borderBottom: scrolled ? '1px solid #44403C' : '1px solid transparent',
                    transition: 'background-color 0.3s ease, border-color 0.3s ease',
                }}
            >
                <div
                    style={{
                        maxWidth: '1400px',
                        margin: '0 auto',
                        padding: '0 24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: '72px',
                    }}
                >
                    {/* Brand */}
                    <h1
                        onClick={() => navigate('/template11')}
                        style={{
                            fontFamily: "'Oswald', sans-serif",
                            fontSize: '26px',
                            fontWeight: 700,
                            color: '#D97706',
                            cursor: 'pointer',
                            letterSpacing: '4px',
                            textTransform: 'uppercase',
                            margin: 0,
                        }}
                    >
                        VEER
                    </h1>

                    {/* Desktop Nav */}
                    <nav
                        style={{
                            display: 'none',
                            gap: '32px',
                            alignItems: 'center',
                        }}
                        className="veer-desktop-nav"
                    >
                        {navLinks.map((link) => (
                            <button
                                key={link.path}
                                onClick={() => handleNavClick(link.path)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontFamily: "'Oswald', sans-serif",
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    letterSpacing: '2px',
                                    textTransform: 'uppercase',
                                    color: '#FAFAF9',
                                    cursor: 'pointer',
                                    padding: '4px 0',
                                    transition: 'color 0.3s ease',
                                    borderBottom: '2px solid transparent',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.color = '#D97706';
                                    e.currentTarget.style.borderBottomColor = '#D97706';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.color = '#FAFAF9';
                                    e.currentTarget.style.borderBottomColor = 'transparent';
                                }}
                            >
                                {link.label}
                            </button>
                        ))}
                    </nav>

                    {/* Right Icons */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <button
                            onClick={() => navigate('/template11/shop')}
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#FAFAF9',
                                padding: '4px',
                                display: 'none',
                            }}
                            className="veer-desktop-icon"
                        >
                            <Search size={20} />
                        </button>
                        <button
                            onClick={() => navigate('/template11/profile')}
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#FAFAF9',
                                padding: '4px',
                                display: 'none',
                            }}
                            className="veer-desktop-icon"
                        >
                            <User size={20} />
                        </button>
                        <button
                            onClick={() => navigate('/template11/cart')}
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#FAFAF9',
                                padding: '4px',
                                position: 'relative',
                            }}
                        >
                            <ShoppingCart size={20} />
                            {cartCount > 0 && (
                                <span
                                    style={{
                                        position: 'absolute',
                                        top: '-4px',
                                        right: '-4px',
                                        backgroundColor: '#D97706',
                                        color: '#1C1917',
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        width: '18px',
                                        height: '18px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {cartCount}
                                </span>
                            )}
                        </button>

                        {/* Mobile Menu Toggle */}
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#FAFAF9',
                                padding: '4px',
                            }}
                            className="veer-mobile-toggle"
                        >
                            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile Overlay Menu */}
            {mobileMenuOpen && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 49,
                        backgroundColor: '#1C1917',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '32px',
                    }}
                >
                    {navLinks.map((link) => (
                        <button
                            key={link.path}
                            onClick={() => handleNavClick(link.path)}
                            style={{
                                background: 'none',
                                border: 'none',
                                fontFamily: "'Oswald', sans-serif",
                                fontSize: '24px',
                                fontWeight: 600,
                                letterSpacing: '4px',
                                textTransform: 'uppercase',
                                color: '#FAFAF9',
                                cursor: 'pointer',
                                transition: 'color 0.3s ease',
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#D97706')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#FAFAF9')}
                        >
                            {link.label}
                        </button>
                    ))}
                    <div style={{ width: '40px', height: '2px', backgroundColor: '#D97706', margin: '8px 0' }} />
                    <div style={{ display: 'flex', gap: '24px' }}>
                        <button
                            onClick={() => handleNavClick('/template11/profile')}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#A8A29E' }}
                        >
                            <User size={22} />
                        </button>
                        <button
                            onClick={() => handleNavClick('/template11/login')}
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontFamily: "'Oswald', sans-serif",
                                fontSize: '14px',
                                fontWeight: 500,
                                letterSpacing: '2px',
                                textTransform: 'uppercase',
                                color: '#D97706',
                            }}
                        >
                            LOGIN
                        </button>
                    </div>
                </div>
            )}

            <style>{`
                @media (min-width: 768px) {
                    .veer-desktop-nav { display: flex !important; }
                    .veer-desktop-icon { display: block !important; }
                    .veer-mobile-toggle { display: none !important; }
                }
            `}</style>
        </>
    );
};

export default Header11;
